<script setup lang="ts">
/**
 * ResidentApprovalsView — /residents/approvals
 *
 * Shows pending/approved/denied approval queues and an audit log.
 * The dual-approval flow is the most critical path: Ryan Thompson's card
 * shows a FlagCallout and requires sign-off from two different board members.
 */
import { ref, onMounted, computed } from 'vue';
import BaseCard from '@/components/ui/BaseCard.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseTabs from '@/components/ui/BaseTabs.vue';
import StatCard from '@/components/ui/StatCard.vue';
import ApprovalCard from '@/components/approvals/ApprovalCard.vue';
import ApprovedResidentRow from '@/components/approvals/ApprovedResidentRow.vue';
import { useResidentsStore } from '@/stores/residents';
import type { ApproveResult } from '@/stores/residents';

const store = useResidentsStore();

// ── Tab config ────────────────────────────────────────────────────────────────

type ApprovalsTab = 'pending' | 'approved' | 'denied' | 'audit';

const activeTab = ref<ApprovalsTab>('pending');

const tabs = computed(() => [
  { key: 'pending' as ApprovalsTab, label: 'Pending', count: store.pendingCount },
  { key: 'approved' as ApprovalsTab, label: 'Approved', count: store.approvedCount },
  { key: 'denied' as ApprovalsTab, label: 'Denied', count: store.deniedCount },
  { key: 'audit' as ApprovalsTab, label: 'Audit log' },
]);

// ── Deny modal state ──────────────────────────────────────────────────────────

const denyingId = ref<string | null>(null);
const denyReason = ref('');
const isDenying = ref(false);

// ── Toast ─────────────────────────────────────────────────────────────────────

const toast = ref<{ message: string; variant: 'success' | 'info' } | null>(null);
let toastTimer: ReturnType<typeof setTimeout> | null = null;

function showToast(message: string, variant: 'success' | 'info' = 'success') {
  toast.value = { message, variant };
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = null; }, 3500);
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(async () => {
  await store.fetchPending();
});

async function onTabChange(key: string) {
  activeTab.value = key as ApprovalsTab;
  if (key === 'approved') await store.fetchApproved();
  if (key === 'denied') await store.fetchDenied();
  if (key === 'audit') await store.fetchAuditLog();
}

// ── Actions ───────────────────────────────────────────────────────────────────

async function onApprove(id: string) {
  try {
    const result: ApproveResult = await store.approve(id);
    if (result === 'Approved') {
      showToast('Resident approved and access granted.');
    } else if (result === 'AwaitingSecondApprover') {
      showToast('First approval recorded. Awaiting second board member sign-off.', 'info');
    } else if (result === 'AlreadyApproved') {
      showToast('You already approved this resident.', 'info');
    }
  } catch (e) {
    showToast(e instanceof Error ? e.message : 'Approval failed.');
  }
}

function openDenyModal(id: string) {
  denyingId.value = id;
  denyReason.value = '';
}

async function confirmDeny() {
  if (!denyingId.value) return;
  isDenying.value = true;
  try {
    await store.deny(denyingId.value, denyReason.value || 'Denied by board member.');
    showToast('Resident denied.');
    denyingId.value = null;
  } catch (e) {
    showToast(e instanceof Error ? e.message : 'Deny failed.');
  } finally {
    isDenying.value = false;
  }
}

async function onBulkApprove() {
  const ids = Array.from(store.selectedIds);
  for (const id of ids) {
    await onApprove(id);
  }
  store.clearSelection();
}
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="resident-approvals-view">
    <!-- Page header -->
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="text-page text-fg font-semibold">Resident approvals</h1>
        <p class="text-caption text-muted mt-0.5">
          <span class="font-mono tabular-nums text-amber-700">●</span>
          <span class="font-mono tabular-nums ml-1">{{ store.pendingCount }}</span> pending review
          <template v-if="store.flaggedCount > 0">
            · <span class="text-orange-600 font-medium font-mono tabular-nums">{{ store.flaggedCount }}</span>
            <span class="text-orange-600 font-medium"> flagged</span>
          </template>
        </p>
      </div>
      <BaseButton variant="secondary" size="sm">
        <template #left>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
            <path d="M3 3v18h18" />
            <path d="M7 14l4-4 4 4 5-5" />
          </svg>
        </template>
        Approval policy
      </BaseButton>
    </div>

    <!-- Stat strip -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <StatCard
        label="Pending your review"
        :value="store.pendingCount"
        :sub="store.flaggedCount > 0 ? `${store.flaggedCount} flagged · ${store.pendingCount - store.flaggedCount} routine` : 'All routine'"
        data-testid="approvals-pending-stat"
      />
      <StatCard
        label="Approved this month"
        :value="store.approvedCount"
        data-testid="approvals-approved-stat"
      />
      <StatCard
        label="Coverage"
        value="—%"
        sub="Pending backend data"
        data-testid="approvals-coverage-stat"
      />
    </div>

    <!-- Tabs + content card -->
    <BaseCard padding="none">
      <div class="px-4 pt-3">
        <BaseTabs
          :tabs="tabs"
          :active="activeTab"
          @change="onTabChange"
        />
      </div>

      <!-- Loading -->
      <div v-if="store.isLoading" class="p-4 flex flex-col gap-3">
        <div v-for="i in 3" :key="i" class="h-32 bg-canvas-soft rounded animate-pulse" />
      </div>

      <!-- Error -->
      <div v-else-if="store.error" class="p-4 text-center text-red-700 text-body">
        {{ store.error }}
      </div>

      <template v-else>
        <!-- ── PENDING TAB ── -->
        <template v-if="activeTab === 'pending'">
          <div class="p-4 pb-2">
            <!-- Bulk action bar -->
            <div
              class="flex items-center gap-3 bg-navy-50 border border-navy-100 rounded-md px-3.5 py-2.5 mb-3.5 flex-wrap"
              data-testid="bulk-action-bar"
            >
              <input
                type="checkbox"
                class="w-4 h-4 accent-navy-700"
                title="Select all routine approvals"
                :checked="store.selectedIds.size > 0 && store.canBulkApprove"
                @change="store.selectedIds.size > 0 ? store.clearSelection() : undefined"
              >
              <span class="text-body font-semibold text-navy-800">
                Select all
                <span class="font-mono tabular-nums">{{ store.pendingCount - store.flaggedCount }}</span>
                routine approvals
              </span>
              <span class="text-muted text-caption">— flagged residents need individual review</span>
              <div class="flex items-center gap-2 ml-auto">
                <button
                  :disabled="!store.canBulkApprove"
                  :class="[
                    'h-8 px-3 text-caption rounded-md border transition-colors font-medium',
                    store.canBulkApprove
                      ? 'bg-navy-800 text-white hover:bg-navy-900 border-navy-800'
                      : 'bg-canvas-soft text-muted border-border cursor-not-allowed',
                  ]"
                  data-testid="bulk-approve-btn"
                  @click="onBulkApprove"
                >
                  Approve selected
                </button>
                <button
                  :disabled="store.selectedIds.size === 0"
                  class="h-8 px-3 text-caption text-muted border border-border rounded-md hover:bg-canvas-soft transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Deny selected
                </button>
              </div>
            </div>

            <!-- Approval cards -->
            <div v-if="store.pending.length === 0" class="py-8 text-center text-muted text-body">
              No pending approvals.
            </div>
            <ApprovalCard
              v-for="req in store.pending"
              :key="req.id"
              :request="req"
              :is-selected="store.selectedIds.has(req.id)"
              @select="store.toggleSelect"
              @approve="onApprove"
              @deny="openDenyModal"
            />
          </div>
        </template>

        <!-- ── APPROVED TAB ── -->
        <template v-else-if="activeTab === 'approved'">
          <div v-if="store.approved.length === 0" class="p-8 text-center text-muted text-body">
            No approved residents yet.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full min-w-[640px]">
              <thead class="bg-canvas-soft border-b border-border">
                <tr>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Resident</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Property</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Verification</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Status</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Date</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Approved by</th>
                </tr>
              </thead>
              <tbody>
                <ApprovedResidentRow
                  v-for="req in store.approved"
                  :key="req.id"
                  :request="req"
                />
              </tbody>
            </table>
          </div>
        </template>

        <!-- ── DENIED TAB ── -->
        <template v-else-if="activeTab === 'denied'">
          <div v-if="store.denied.length === 0" class="p-8 text-center text-muted text-body">
            No denied residents.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full min-w-[640px]">
              <thead class="bg-canvas-soft border-b border-border">
                <tr>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Resident</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Property</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Verification</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Status</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Date</th>
                  <th class="px-5 py-2.5 text-left text-caption font-semibold text-muted-2 uppercase tracking-wide">Reviewed by</th>
                </tr>
              </thead>
              <tbody>
                <ApprovedResidentRow
                  v-for="req in store.denied"
                  :key="req.id"
                  :request="req"
                />
              </tbody>
            </table>
          </div>
        </template>

        <!-- ── AUDIT LOG TAB ── -->
        <template v-else-if="activeTab === 'audit'">
          <div v-if="store.auditLog.length === 0" class="p-8 text-center text-muted text-body">
            No audit log entries yet.
          </div>
          <div v-else class="divide-y divide-border">
            <div
              v-for="entry in store.auditLog"
              :key="entry.id"
              class="px-5 py-3.5 flex items-start gap-3"
            >
              <div class="w-2 h-2 rounded-pill bg-navy-500 mt-1.5 flex-shrink-0" />
              <div class="flex-1 min-w-0">
                <p class="text-body text-fg">{{ entry.body }}</p>
                <p class="text-caption text-muted mt-0.5 font-mono tabular-nums">
                  {{ entry.actorName }} ·
                  {{ new Date(entry.createdAt).toLocaleString() }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- Approval policy footer -->
      <div class="px-4 py-3 border-t border-border bg-canvas-soft">
        <p class="text-caption text-muted">
          Approval policy: mailed code verification required for all new residents.
          Residents with prior bylaw violations require two-board-member sign-off.
        </p>
      </div>
    </BaseCard>

    <!-- Toast notification -->
    <Teleport to="body">
      <div
        v-if="toast"
        :class="[
          'fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-md text-body font-medium text-white max-w-xs',
          toast.variant === 'success' ? 'bg-green-600' : 'bg-navy-800',
        ]"
        role="alert"
      >
        {{ toast.message }}
      </div>
    </Teleport>
  </div>

  <!-- Deny confirmation modal -->
  <Teleport to="body">
    <div
      v-if="denyingId"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-fg/30"
      @click.self="denyingId = null"
    >
      <div class="bg-surface border border-border rounded-xl shadow-lg w-full max-w-sm p-5 flex flex-col gap-4">
        <div class="text-card text-fg font-semibold">Deny resident</div>
        <label class="flex flex-col gap-1.5">
          <span class="text-caption font-semibold text-fg-soft uppercase tracking-wide">Reason (optional)</span>
          <textarea
            v-model="denyReason"
            rows="3"
            placeholder="Describe the reason for denial…"
            class="px-3 py-2 text-body border border-border rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none"
            data-testid="deny-reason-input"
          />
        </label>
        <div class="flex justify-end gap-2">
          <BaseButton variant="ghost" size="md" @click="denyingId = null">Cancel</BaseButton>
          <BaseButton
            variant="danger"
            size="md"
            :loading="isDenying"
            data-testid="confirm-deny-btn"
            @click="confirmDeny"
          >
            Confirm deny
          </BaseButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>
